import { SerializedDrawing } from 'lightweight-charts-drawing';

interface StoredPayload {
  v: number;
  savedAt: number;
  drawings: SerializedDrawing[];
}

const VERSION = 1;
const MAX_DRAWINGS_PER_KEY = 200;

/**
 * Persistencia de dibujos en localStorage con guardado debounced.
 * La clave la aporta el host (símbolo + timeframe), por lo que el formato
 * de `time` es consistente dentro de cada clave.
 */
export class DrawingStorage {
  private saveTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly debounceMs = 500) {}

  public scheduleSave(key: string | null, provider: () => SerializedDrawing[]): void {
    if (!key) return;
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => {
      this.saveTimer = null;
      this.saveNow(key, provider());
    }, this.debounceMs);
  }

  public saveNow(key: string | null, drawings: SerializedDrawing[]): void {
    if (!key) return;
    const payload: StoredPayload = {
      v: VERSION,
      savedAt: Date.now(),
      drawings: drawings.slice(0, MAX_DRAWINGS_PER_KEY),
    };
    try {
      if (payload.drawings.length === 0) localStorage.removeItem(key);
      else localStorage.setItem(key, JSON.stringify(payload));
    } catch (e) {
      console.warn('[chart-drawing] no se pudo guardar en localStorage (¿cuota llena?)', e);
    }
  }

  public load(key: string | null): SerializedDrawing[] {
    if (!key) return [];
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return [];
      const payload = JSON.parse(raw) as StoredPayload;
      if (payload?.v !== VERSION || !Array.isArray(payload.drawings)) return [];
      return payload.drawings;
    } catch (e) {
      console.warn('[chart-drawing] no se pudo leer de localStorage', e);
      return [];
    }
  }

  public cancelPending(): void {
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
    }
  }
}
